import { PlanTypeCardProgressStage } from '@ebalance/proto/bffuser/v1/submission_management_model_pb'

import type { StatusBadgeVariant } from '~/models/planSubmission/statusBadge'

/**
 * UNSPECIFIED を除いた進捗段。契約上 `submission_summary` は `cards` の全要素で
 * 必ず set され、`progress_stage` は 6 値のいずれかに決まる。例外は
 * `no_submission_record` が true の時 (AC-PSM-078、版の無い通知だけの計画) で、
 * その時だけ UNSPECIFIED が来て主バッジを出さない
 * (Design Note `submission-card-header-status` Contract/Data)。proto の型は
 * zero 値を含むため、`models/planTypeCard.ts` の `KnownPlanTypeCardKind` と
 * 同型で防御的に型を分離する。
 */
type KnownProgressStage = Exclude<
  PlanTypeCardProgressStage,
  PlanTypeCardProgressStage.UNSPECIFIED
>

/**
 * カードヘッダーの主バッジ: `progress_stage` (6 値、Decision 2) → 既存
 * `PlanSubmissionStatusBadge` の該当 variant の見た目を流用する写像
 * (Design Note UI「Design gaps」: 新規 UI を発明しない)。
 */
const PROGRESS_STAGE_BADGE_VARIANT: Record<
  KnownProgressStage,
  StatusBadgeVariant
> = {
  [PlanTypeCardProgressStage.GENERATING]: 'generating',
  [PlanTypeCardProgressStage.UNSUBMITTED]: 'unsubmitted',
  [PlanTypeCardProgressStage.APPROVAL_PENDING]: 'approvalPending',
  [PlanTypeCardProgressStage.SUBMITTING]: 'submitting',
  [PlanTypeCardProgressStage.SUBMITTED]: 'submitted',
  [PlanTypeCardProgressStage.ACCEPTED]: 'accepted',
}

/**
 * `progress_stage` から主バッジの variant を導出する。UNSPECIFIED は
 * `no_submission_record` が true の時の契約値 (AC-PSM-078: 主バッジなし) で、
 * summary 欠落時の防御でもある。いずれも `isKnownPlanTypeCardKind` と同型で
 * `undefined` を返して主バッジ自体を出さない (「未提出」等の具体的な variant
 * へ捏造しない — 未提出は業務上の実状態であり、未知の値をそれに化けさせると
 * 誤情報になる)。呼び出し元は `undefined` の時に Badge を描画しない。
 */
export function deriveProgressStageBadgeVariant(
  stage: PlanTypeCardProgressStage,
): StatusBadgeVariant | undefined {
  if (stage === PlanTypeCardProgressStage.UNSPECIFIED) {
    return undefined
  }
  return PROGRESS_STAGE_BADGE_VARIANT[stage]
}
